import React, { useState, useEffect, useContext } from 'react';

import CheckmarkSmallSvg from '@/components/common/CheckmarkSmallSvg';
import ImportantSvg from '@/components/common/ImportantSvg';
import TooltipClose from '@/components/common/TooltipCloseSvg';
import { ensureReCaptchaReady } from '@/external/service/paypal.service';

import { CartContext } from '../../contexts/CartContext';
import { TmAddress } from '../../tscapi/model/account';
import { TmAccountRequest } from '../../tscapi/model/request/account-request';
import { AccountUtils } from '../../tscapi/utils/account-utils';
import { Cookie } from '../../tscapi/utils/cookie';
import { getGuestEmail } from '../signin/utils/sessionStorage';

import { AddressForm } from './address.form';
import { EmailForm } from './email.form';
declare let grecaptcha: any;

declare global {
    interface Window {
        onloadCallback: any;
    }
}
interface CreateAccountProps {
    setCheckoutStep: (string) => void;
}
export const CreateAccount = ({ setCheckoutStep }: CreateAccountProps) => {
    const {
        submitCreateAccount,
        setSubmitCreateAccount,
        createCustomerAccount,
        createAccountErrorMessage,
        setCreateAccountErrorMessage,
    } = useContext(CartContext);
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [formState, setFormState] = useState('');
    const [emailFormState, setEmailFormState] = useState('');
    const [isExistingCustomerGuest, setIsExistingCustomerGuest] = useState(false);
    const [emailData, setEmailData] = useState({
        Password: '',
        Password2: '',
        Offers: false,
        CreateAccount: true,
    });
    const [addressToAdd, setAddressToAdd] = useState<TmAddress>({
        Id: 0,
        FirstName: '',
        LastName: '',
        DayPhone: '',
        Street: '',
        AddressRef1: '',
        City: '',
        State: 'ON',
        ZipCode: '',
        IsDefault: true,
        POBoxNumber: '',
        IsPOBox: false,
        SalesForceAddressId: '',
    });
    const cookie: Cookie = new Cookie();
    const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
    const shouldValidateEmailForm = !isExistingCustomerGuest;

    const scrollToTop = () => {
        // Scroll to the error message container if it exists
        setTimeout(() => {
            const errorContainer = document.querySelector('.notice-card, .alert-danger');
            if (errorContainer) {
                // Adjust scroll position to account for sticky header
                const isMobile = window.innerWidth < 768;
                const headerOffset = isMobile ? 80 : 5; //80 is mobile sticky header, 5 gives slignt buffer on desktop
                const elementPosition = errorContainer.getBoundingClientRect().top + window.scrollY;
                const offsetPosition = elementPosition - headerOffset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                });
            } else {
                // Fallback to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }, 100);
    };

    useEffect(() => {
        const reCaptchaSiteKey = window.tscConfig['GoogleReCaptchaV3SiteKey'] as string;
        ensureReCaptchaReady(reCaptchaSiteKey).catch((error) =>
            console.error('Error initializing reCAPTCHA globally:', error),
        );

        const existingCustomerGuest = sessionStorage.getItem('TSC_ExistingCustomerGuest') === 'true';
        if (existingCustomerGuest) {
            setIsExistingCustomerGuest(true);
            setEmailData((prev) => ({
                ...prev,
                CreateAccount: false,
            }));
            sessionStorage.removeItem('TSC_ExistingCustomerGuest');
        }
    }, []);

    useEffect(() => {
        if (submitCreateAccount) {
            setFormState('validate');
            setEmailFormState(shouldValidateEmailForm ? 'validate' : 'submit');
            setSubmitCreateAccount(false);
        }
    }, [submitCreateAccount, shouldValidateEmailForm]);

    useEffect(() => {
        if (recaptchaToken) {
            const createAccountAndSignIn = async () => {
                const inputAddressData = addressToAdd;
                const cartAddressToAdd = AccountUtils.formatAddressData(inputAddressData);

                const accountRequest = new TmAccountRequest();
                accountRequest.IsPhoneCustomer = false;
                accountRequest.Email = getGuestEmail();
                accountRequest.Subscribe = emailData.Offers;

                const isGuestAccount = !emailData.CreateAccount;
                accountRequest.IsGuest = isGuestAccount;
                if (!isGuestAccount) {
                    accountRequest.Password = emailData.Password;
                    accountRequest.RetypedPassword = emailData.Password2;
                }
                accountRequest.CustomerNumber = '';
                accountRequest.FirstName = cartAddressToAdd.FirstName;
                accountRequest.LastName = cartAddressToAdd.LastName;
                accountRequest.Phone = cartAddressToAdd.DayPhone;
                accountRequest.Address1 = cartAddressToAdd.Street;
                accountRequest.Address2 = cartAddressToAdd.AddressRef1;
                accountRequest.IsPOBox = false;
                accountRequest.POBox = '';
                accountRequest.City = cartAddressToAdd.City;
                accountRequest.Province = cartAddressToAdd.State;
                accountRequest.PostalCode = cartAddressToAdd.ZipCode;
                accountRequest.ReCaptchaToken = recaptchaToken;
                try {
                    const analyticsData = window.analyticsData || {};
                    analyticsData.signUpOptions = analyticsData.signUpOptions || {};
                    analyticsData.signUpOptions.Offers = emailData.Offers;
                    analyticsData.signUpOptions.tscAccount = emailData.CreateAccount;

                    setRecaptchaToken('');
                    await createCustomerAccount(accountRequest);

                    if (emailData.Offers) {
                        const subscribeEvent = new CustomEvent('subscribe', {
                            detail: {
                                Source: 'ExpressGuestCheckout',
                            },
                        });
                        document.dispatchEvent(subscribeEvent);
                    }

                    const checkoutPixelEvent = new CustomEvent('complete-registration', { detail: addressToAdd });
                    document.dispatchEvent(checkoutPixelEvent);
                    setRecaptchaToken('');
                    setCheckoutStep('payment');
                } catch (error) {
                    const code = error.response?.data[0]?.Code;
                    const message = error.response?.data[0]?.Message;
                    if (code === 'GuestDuplicateAccountError' || code === 'GuestDuplicateFlaggedAccountError') {
                        setCreateAccountErrorMessage(code);
                    } else {
                        setCreateAccountErrorMessage(message);
                    }
                    scrollToTop();
                }
            };

            createAccountAndSignIn();
        }
    }, [recaptchaToken]);

    useEffect(() => {
        if (formState == 'submit' && (!shouldValidateEmailForm || emailFormState == 'submit')) {
            setCreateAccountErrorMessage('');

            if (!addressToAdd || !emailData) {
                setCreateAccountErrorMessage('Please enter required information');
                scrollToTop();
                return;
            }

            grecaptcha
                .execute(reCaptchaSiteKey, { action: 'createaccountcheckout' })
                .then((token) => setRecaptchaToken(token));
        }
    }, [formState, emailFormState, shouldValidateEmailForm]);

    const handleSigninClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        cookie.TargetUrl = '/pages/expresscheckout';
        window.location.href = '/pages/signin';
    };

    useEffect(() => {
        if (createAccountErrorMessage) {
            scrollToTop();
        }
    }, [createAccountErrorMessage]);

    return (
        <div style={{ marginTop: '10px' }} className="create-address__wrap">
            {createAccountErrorMessage && (
                <>
                    {createAccountErrorMessage === 'GuestDuplicateAccountError' && (
                        <div className="notice-card" style={{ border: '2px solid #01b9bb' }}>
                            <div className="notice-card__icon">
                                <ImportantSvg />
                            </div>
                            <div className="notice-card__message">
                                <b>An account already exists with the email address provided.</b> Please{' '}
                                <a href="/pages/signin" onClick={handleSigninClick}>
                                    sign in
                                </a>{' '}
                                or <a href="/pages/passwordrequest">reset your password</a> to access and enjoy the
                                benefits of your TSC account:
                                <ul className="notice-card__list">
                                    <li>
                                        <CheckmarkSmallSvg />
                                        <span>Checkout faster</span>
                                    </li>
                                    <li>
                                        <CheckmarkSmallSvg />
                                        <div>
                                            Split your purchase price over interest free payments with{' '}
                                            <a href="/pages/customer-service/easy-pay" style={{ fontWeight: 400 }}>
                                                Easy Pay
                                            </a>
                                        </div>
                                    </li>
                                    <li>
                                        <CheckmarkSmallSvg />
                                        <div>
                                            Enjoy personalized recommendations tailored to your your shopping
                                            preferences
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                    {createAccountErrorMessage === 'GuestDuplicateFlaggedAccountError' && (
                        <div className="notice-card" style={{ border: '2px solid #df0c0c' }}>
                            <div className="notice-card__icon">
                                <ImportantSvg />
                            </div>
                            <div className="notice-card__message">
                                <b>An account already exists with the email address provided.</b> To proceed, please
                                contact our Customer Service Centre at 1-888-2020-888 to inquire about the status. A TSC
                                account lets you:
                                <ul className="notice-card__list">
                                    <li>
                                        <CheckmarkSmallSvg />
                                        <span>Checkout faster</span>
                                    </li>
                                    <li>
                                        <CheckmarkSmallSvg />
                                        <div>
                                            Split your purchase price over interest free payments with{' '}
                                            <a href="/pages/customer-service/easy-pay" style={{ fontWeight: 400 }}>
                                                Easy Pay
                                            </a>
                                        </div>
                                    </li>
                                    <li>
                                        <CheckmarkSmallSvg />
                                        <div>
                                            Enjoy personalized recommendations tailored to your your shopping
                                            preferences
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                    {createAccountErrorMessage !== 'GuestDuplicateAccountError' &&
                        createAccountErrorMessage !== 'GuestDuplicateFlaggedAccountError' && (
                            <div className="alert alert-danger">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    className="form__error-svg"
                                >
                                    <path d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
                                </svg>
                                {createAccountErrorMessage}
                            </div>
                        )}
                </>
            )}

            <AddressForm
                selectedAddress={addressToAdd}
                setSelectedAddress={setAddressToAdd}
                formState={formState}
                setFormState={setFormState}
                isExistingGuest={isExistingCustomerGuest}
                setErrorMessage={setCreateAccountErrorMessage}
            />

            {!isExistingCustomerGuest && (
                <EmailForm
                    emailDataProp={emailData}
                    setEmailDataProp={setEmailData}
                    formState={emailFormState}
                    setFormState={setEmailFormState}
                    setErrorMessage={setCreateAccountErrorMessage}
                />
            )}
        </div>
    );
};
